import React, { useState } from 'react';
import { Loader2, TrendingDown, Wrench, ShieldCheck, Headphones, ArrowRight } from 'lucide-react';
import { HomeNav } from '../components/landing/home/HomeNav';
import { HomeFooter } from '../components/landing/home/HomeFooter';
import { usePageMeta } from '../lib/usePageMeta';
import { useMarketingViewport } from '../components/landing/home/useMarketingViewport';
import '../components/landing/home/home.css';
import '../components/landing/home/home-readable.css';
import '../components/landing/home/agency.css';
import '../components/landing/payg/payg.css';
import '../components/landing/payg/enterprise.css';

const API = process.env.REACT_APP_BACKEND_URL;
const BENEFITS = [
  [TrendingDown, 'Volume pricing', 'Lower per-minute and per-message rates as you grow.'],
  [Wrench, 'Done-for-you builds', 'Our team designs, builds and launches agents for your clients.'],
  [ShieldCheck, 'HIPAA compliance', 'Secure data handling and compliant call flows for healthcare clients.'],
  [Headphones, 'Dedicated support', 'A named contact, VA pool access and higher concurrent call limits.'],
];
const STEPS = [['Tell us about you', 'Two minutes, a few questions.'], ['Book a call', 'Pick a time right after you submit.'], ['Get your plan', 'Custom rates and a rollout plan for your agency.']];
const EMPTY = { first_name: '', last_name: '', email: '', phone: '', clients: '1-5', usage: 'Agency serving clients', use_case: '', heard_from: '' };

const Field = ({ label, k, v, set, type = 'text', full, as, options, placeholder }) => (
  <label className={full ? 'en-full' : ''}><span>{label}</span>
    {as === 'select' ? <select value={v[k]} onChange={e => set({ ...v, [k]: e.target.value })} data-testid={`enterprise-${k}`}>{options.map(o => <option key={o}>{o}</option>)}</select>
      : as === 'textarea' ? <textarea rows={3} required placeholder={placeholder} value={v[k]} onChange={e => set({ ...v, [k]: e.target.value })} data-testid={`enterprise-${k}`} />
        : <input type={type} required placeholder={placeholder} value={v[k]} onChange={e => set({ ...v, [k]: e.target.value })} data-testid={`enterprise-${k}`} />}
  </label>
);

const LeadForm = ({ v, setV, busy, error, onSubmit }) => (
  <form className="en-form" onSubmit={onSubmit} data-testid="enterprise-form">
    <h2>Talk to our team</h2>
    <p className="en-form-sub">Takes two minutes. Book a call right after.</p>
    <Field label="First name" k="first_name" v={v} set={setV} />
    <Field label="Last name" k="last_name" v={v} set={setV} />
    <Field label="Work email" k="email" type="email" v={v} set={setV} placeholder="you@agency.com" />
    <Field label="Phone" k="phone" type="tel" v={v} set={setV} />
    <Field label="Number of clients" k="clients" as="select" options={['1-5', '6-10', '11-25', '26-50', '50+']} v={v} set={setV} />
    <Field label="How you'll use Voxinity" k="usage" as="select" options={['Agency serving clients', 'My own business', 'Reselling / white-label', 'Other']} v={v} set={setV} />
    <Field label="What do you want to build?" k="use_case" as="textarea" full v={v} set={setV} placeholder="e.g. inbound booking agents for 20 dental clinics" />
    <Field label="How did you hear about us?" k="heard_from" full v={v} set={setV} />
    {error && <p className="en-full en-error" data-testid="enterprise-error">{error}</p>}
    <button type="submit" className="en-full en-submit" disabled={busy} data-testid="enterprise-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : null}Talk to our team<ArrowRight size={16} /></button>
  </form>
);

export default function EnterprisePage() {
  useMarketingViewport();
  usePageMeta('Enterprise — Voxinity', 'Talk to our team about Enterprise: volume rates, done-for-you builds, HIPAA compliance and dedicated support.');
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState('');
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/enterprise/inquiry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v) });
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check the form and try again.');
      setBooking(data.booking_url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return <div className="homepage agency-site" data-testid="enterprise-page"><HomeNav />
    <main className="en-page">
      <section className="en-hero"><div className="hm-container en-grid">
        <div className="en-copy">
          <p className="py-eyebrow">Enterprise</p>
          <h1 data-testid="enterprise-headline">Built for agencies<br /><em>running at scale.</em></h1>
          <p className="en-lead">Custom rates, compliance and a team that builds with you. Everything in Pay as you go, plus:</p>
          <div className="en-benefits">{BENEFITS.map(([Icon, t, d]) => (
            <div key={t} className="en-benefit" data-testid={`enterprise-benefit-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}><Icon size={18} /><h3>{t}</h3><p>{d}</p></div>))}
          </div>
        </div>
        <div className="en-card">
          {booking ? <div data-testid="enterprise-booking">
            <p className="en-thanks">Thanks, {v.first_name}. Pick a time below.</p>
            <iframe src={booking} title="Book a call" allow="payment" className="en-iframe" data-testid="enterprise-booking-iframe" />
          </div> : <LeadForm v={v} setV={setV} busy={busy} error={error} onSubmit={submit} />}
        </div>
      </div></section>
      <section className="en-steps-wrap"><div className="hm-container">
        <p className="py-eyebrow">What happens next</p>
        <ol className="en-steps">{STEPS.map(([t, d], i) => <li key={t} data-testid={`enterprise-step-${i + 1}`}><span>{String(i + 1).padStart(2, '0')}</span><h3>{t}</h3><p>{d}</p></li>)}</ol>
      </div></section>
    </main><HomeFooter />
  </div>;
}
